import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import Notification from 'components/Notification';
import Panel from 'components/Panel';
import {MoneyRequest} from 'pages/Portal/ClientInboxItem/types';
import PayButton from 'pages/Portal/components/PayButton';
import useModalWorkflow from 'pages/Portal/components/PayButton/use-modal-workflow';
import useRedirectWorkflow from 'pages/Portal/components/PayButton/use-redirect-workflow';
import {toCurrency} from 'utils/number';

interface PaymentPanelProps {
    moneyRequest: MoneyRequest;
}

const PaymentPanel: FC<PaymentPanelProps> = ({moneyRequest}) => {
    const {t} = useTranslation('Portal');

    const usePay = moneyRequest.useRedirect
        ? useRedirectWorkflow
        : useModalWorkflow;

    const {errorMessage, isLoading, isPaid, onClick} = usePay({
        moneyRequest,
    });

    if (moneyRequest.cancelled) {
        return null;
    }

    return (
        <Panel title={t('clientInboxItem.payPanel.title')}>
            <div className="flex flex-col gap-y-3">
                <div className="flex justify-between font-medium">
                    <span>{t('clientInboxItem.payPanel.paymentTotal')}</span>
                    <span>
                        {toCurrency({
                            currency: moneyRequest.currency,
                            value: moneyRequest.totalCost,
                        })}
                    </span>
                </div>
                {isPaid ? (
                    <Notification type="success">
                        {t('clientInboxItem.payPanel.successMessage')}
                    </Notification>
                ) : (
                    <>
                        <PayButton isLoading={isLoading} onClick={onClick} />
                        {moneyRequest.autoChargeEnabled && (
                            <div className="text-sm">
                                {t(
                                    'clientInboxItem.payPanel.autoChargeEnabledMessage',
                                )}
                            </div>
                        )}
                    </>
                )}
                {errorMessage && (
                    <Notification type="danger">{errorMessage}</Notification>
                )}
            </div>
        </Panel>
    );
};

export default PaymentPanel;
